// ============================================================================
// KYC SERVICE (Didit)
// ----------------------------------------------------------------------------
// Identity verification through Didit's hosted flow. Replaces Stripe Identity.
// The server endpoint returns { sessionId, url }; the client just redirects.
// Status lives in Firestore (kycVerifications/{uid}) and is read the same way
// as before, so nothing else in the app changes.
// ============================================================================
import { auth } from './firebase';
import { API_BASE } from '../utils/apiBase';

export interface KycSession { sessionId: string; url: string; expiresAt: string | null }

async function authed(path: string, init: RequestInit = {}): Promise<Response> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const token = await user.getIdToken();
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers || {}) },
  });
}

export async function createVerificationSession(returnUrl?: string): Promise<KycSession | { error: string }> {
  try {
    const r = await authed('/didit/createSession', { method: 'POST', body: JSON.stringify({ returnUrl: returnUrl || `${window.location.origin}/wallet?verification=complete` }) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { error: data?.error || `Could not start verification (server answered ${r.status})` };
    return { sessionId: data.sessionId, url: data.url, expiresAt: data.expiresAt ?? null };
  } catch (e: any) {
    return { error: e?.message || 'Could not start verification' };
  }
}

/** After the player returns: ask our server to pull the decision from Didit now. */
export async function syncSession(sessionId?: string | null): Promise<{ status: string } | { error: string }> {
  try {
    const r = await authed('/didit/syncSession', { method: 'POST', body: JSON.stringify(sessionId ? { sessionId } : {}) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { error: data?.error || `Could not check verification (server answered ${r.status})` };
    return { status: data.status || 'pending' };
  } catch (e: any) {
    return { error: e?.message || 'Could not check verification' };
  }
}

/**
 * Ask the server to check the player's open crypto orders against
 * NOWPayments and credit any that are paid. Lives here because it is the
 * same "pull the truth from the provider" pattern as syncSession.
 */
export async function reconcileCryptoOrders(orderId?: string): Promise<{ credited: boolean; results: Array<{ orderId: string; result: string; paymentStatus?: string }> } | { error: string }> {
  try {
    const r = await authed('/nowpayments/reconcile', { method: 'POST', body: JSON.stringify(orderId ? { orderId } : {}) });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) return { error: data?.error || `Could not check the purchase (server answered ${r.status})` };
    return { credited: !!data.credited, results: data.results || [] };
  } catch (e: any) {
    return { error: e?.message || 'Could not check the purchase' };
  }
}

/** Hosted flow: leave the app for Didit, they send the player back to returnUrl. */
export function redirectToVerification(url: string): void {
  window.location.href = url;
}

const kycService = { createVerificationSession, redirectToVerification, syncSession, reconcileCryptoOrders };
export default kycService;
